import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, Mic2, Briefcase, Sparkles, Send, ShieldCheck, Download, ArrowRight, ArrowUpRight, Heart } from "lucide-react";
import UpcomingShows from "@/components/UpcomingShows";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { PRESS_KIT_URL } from "@/lib/assets";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import SectionLabel from "@/components/SectionLabel";
import ShareButtons from "@/components/ShareButtons";
import { EpkCredibility } from "@/components/EpkCredibility";
import { HERO_NINA_SECONDARY as BIZ } from "@/lib/assets";

const CLARITY_URL = "https://www.mybizconsulting.net/clarity-assessment";

const TIMELINE = [
  { year: "1991", title: "Career Begins", key: true, body: "Nina enters the game in Philadelphia — the first writes, the first stages, the foundation for everything to come." },
  { year: "1992", title: "First Tour — Lay-D-Bug", body: "Hit the road on her debut tour as Lay-D-Bug, managed by the late Loni Gamble — first manager, mentor, and believer." },
  { year: "1993", title: "First Video On Air", body: "'We Are The Young' became her first music video to air — broadcast as a Comcast commercial." },
  { year: "1994", title: "PA Prison Tour", key: true, body: "Toured Pennsylvania state correctional facilities — performing, listening, and bringing music inside the walls. The defining experience that grounded her artist-as-advocate posture." },
  { year: "1995", title: "First Mixtape — Miss. Relevant", body: "Released her debut mixtape, 'Miss. Relevant.' An independent statement that traveled corner-to-corner across the city." },
  { year: "2001", title: "Phat Farm Years Begin", body: "Tapped to host for Russell Simmons and Phat Farm — working alongside DJ Tayrok across showcases, mixtapes, and brand activations." },
  { year: "2002", title: "Founded Mob Mistress Entertainment", key: true, body: "Officially launched her own label — the imprint that would house every release going forward." },
  { year: "2003", title: "Phat Farm Mixtape Feature", body: "Featured on the Phat Farm Mixtape with DJ Tayrok — a national co-sign during her Russell Simmons tenure." },
  { year: "2005", title: "Steel City Massacre & In Da Streets Radio", key: true, body: "Featured on DJ Cadillac's 'Steel City Massacre Vol. 1' (Jan 25). Same year, founded In Da Streets Radio — the 24/7 global station built for indie artists. The Phat Farm chapter closes; her own platform begins." },
  { year: "2010", title: "My Biz Consulting", key: true, body: "Began advising creators and small businesses on AI-first operations." },
  { year: "2025", title: "Inspire Da Streets", body: "Founded the non-profit for artist mental health & youth empowerment." },
  { year: "2026", title: "OVA 40", key: true, body: "Released the 17-track debut full-length album." },
];

export default function EPK() {
  const [bookingType, setBookingType] = useState("music");
  const [submitting, setSubmitting] = useState(false);
  const [showFullTimeline, setShowFullTimeline] = useState(false);
  const [press, setPress] = useState([]);
  const [osMeta, setOsMeta] = useState(null);
  const [osForm, setOsForm] = useState({ name: "", email: "", phone: "" });
  const [osSubmitting, setOsSubmitting] = useState(false);
  const [osUnlocked, setOsUnlocked] = useState(null);

  const submitOneSheet = async (e) => {
    e.preventDefault();
    if (!osForm.name.trim() || !osForm.email.trim() || !osForm.phone.trim()) {
      toast.error("Please add your name, email, and phone.");
      return;
    }
    setOsSubmitting(true);
    try {
      const r = await api.post("/public/one-sheet/request", osForm);
      setOsUnlocked({
        url: imgUrl(r.data.url),
        filename: r.data.filename,
        pdfUrl: imgUrl(r.data.pdf_url),
        pdfFilename: r.data.pdf_filename,
      });
      toast.success("Thanks! Your one-sheet is ready.");
      const a = document.createElement("a");
      a.href = imgUrl(r.data.pdf_url || r.data.url);
      a.download = r.data.pdf_filename || r.data.filename || "Nina-Capone-One-Sheet.pdf";
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not unlock the one-sheet. Please try again.");
    } finally {
      setOsSubmitting(false);
    }
  };

  useEffect(() => {
    api
      .get("/public/press")
      .then((r) => setPress(r.data.items || []))
      .catch(() => setPress([]));
    api
      .get("/public/one-sheet")
      .then((r) => setOsMeta(r.data))
      .catch(() => {});
  }, []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    event_date: "",
    location: "",
    budget: "",
    message: "",
  });

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const goBook = (type) => {
    setBookingType(type);
    setTimeout(() => scrollTo("contact"), 60);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/public/booking", { ...form, booking_type: bookingType });
      toast.success("Booking request received. Nina's team will respond within 3 business days.");
      setForm({
        name: "",
        email: "",
        phone: "",
        organization: "",
        event_date: "",
        location: "",
        budget: "",
        message: "",
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  const visibleTimeline = showFullTimeline ? TIMELINE : TIMELINE.filter((i) => i.key);

  return (
    <div data-testid="epk-page">
      {/* HERO */}
      <section className="relative min-h-[78vh] flex items-end overflow-hidden">
        <img
          src={imgUrl("/api/static/press/epk-portrait.jpg")}
          alt="EPK & Booking — Nina Capone"
          className="absolute inset-0 w-full h-full object-cover object-[center_18%]"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,3,8,0.15) 0%, rgba(5,3,8,0.05) 30%, rgba(5,3,8,0.65) 70%, #050308 100%)",
          }}
        />
        <div
          className="absolute inset-x-0 bottom-0 h-[55%] pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, rgba(5,3,8,0.85) 55%, #050308 100%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pb-12 pt-24 w-full">
          <div className="max-w-3xl space-y-6 fade-up">
            <h1 className="font-serif font-light tracking-tighter text-[2.8rem] sm:text-6xl lg:text-8xl leading-[0.9]">
              Book{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                Nina Capone
                <sup className="align-super text-[0.35em] tracking-normal opacity-80">™</sup>
              </em>
            </h1>
            <p className="text-white/80 text-base sm:text-lg max-w-xl leading-relaxed">
              Bring the authority, energy, and experience of Nina Capone™ to
              your next event, production, interview, or campaign.
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 max-w-xl text-sm text-white/75" data-testid="epk-services">
              {[
                "Live musical performances",
                "Event hosting and appearances",
                "Keynote and panel speaking",
                "Radio, podcast, and media interviews",
                "Entertainment-industry discussions",
                "Brand partnerships and promotional campaigns",
              ].map((s) => (
                <li key={s} className="flex items-start gap-2.5">
                  <Sparkles size={13} className="mt-1 shrink-0" style={{ color: "var(--gold)" }} />
                  {s}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  scrollTo("contact");
                }}
                className="btn-gold rounded-none inline-flex items-center gap-2.5 px-7 py-4 text-xs uppercase tracking-[0.22em]"
                data-testid="epk-request-booking"
              >
                <Mic2 size={15} strokeWidth={1.6} />
                Request a Booking
              </a>
              <a
                href={PRESS_KIT_URL}
                download
                className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-4 text-xs uppercase tracking-[0.22em]"
                data-testid="epk-download"
              >
                <Download size={15} strokeWidth={1.6} />
                Download EPK (PDF)
              </a>
              <button
                type="button"
                onClick={() => goBook("speaking")}
                className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-4 text-xs uppercase tracking-[0.22em]"
                data-testid="epk-media-inquiry"
              >
                <Send size={15} strokeWidth={1.6} />
                Submit a Media Inquiry
              </button>
            </div>
            <div className="flex items-center gap-3 pt-1" data-testid="epk-share">
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">Share</span>
              <ShareButtons
                shareUrl={`${window.location.origin}/api/og/page/epk`}
                canonicalUrl={`${window.location.origin}/epk`}
                text="Book Nina Capone — EPK & Booking"
                testidPrefix="epk-share"
              />
            </div>
            <div
              className="inline-flex items-center gap-3 px-4 py-2 border"
              style={{
                borderColor: "rgba(232,180,184,0.45)",
                background: "rgba(5,3,8,0.55)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
              }}
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: "var(--gold)" }}
              />
              <p className="eyebrow !text-[0.7rem]">05 · Press Kit</p>
            </div>
          </div>
        </div>
      </section>

      <EpkCredibility />

      {/* ARTIST ONE-SHEET — gated download for press & promoters */}
      <section className="py-24 border-t border-white/5" data-testid="epk-one-sheet">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <SectionLabel label="One-Sheet" />
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mt-10">
            <div className="relative">
              <div className="absolute -inset-3 rounded-lg opacity-40 blur-2xl" style={{ background: "radial-gradient(circle at 50% 30%, rgba(232,180,184,0.35), transparent 70%)" }} />
              <img
                src={imgUrl(osMeta?.thumb_url || "/api/static/press/one-sheet-thumb.jpg")}
                alt="Nina Capone artist one-sheet preview"
                loading="lazy"
                className="relative w-full max-w-md mx-auto lg:mx-0 rounded-lg border border-white/10 shadow-2xl"
                data-testid="one-sheet-preview"
              />
            </div>
            <div className="space-y-6">
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light leading-[1.02]">
                The artist one-sheet.
              </h2>
              <p className="text-white/60 leading-relaxed max-w-lg">
                A single-page press &amp; booking snapshot &mdash; bio, highlights,
                and contact, formatted for promoters, venues, and media. Tell us
                where to send it and the download unlocks instantly.
              </p>
              {osUnlocked ? (
                <div className="surface p-8 space-y-4" data-testid="one-sheet-unlocked">
                  <p className="text-white/80">Your one-sheet is ready.</p>
                  <div className="flex flex-wrap gap-3">
                    <a
                      href={osUnlocked.pdfUrl}
                      download={osUnlocked.pdfFilename}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-gold rounded-none inline-flex items-center gap-2.5 px-7 py-4 text-xs uppercase tracking-[0.22em]"
                      data-testid="one-sheet-download-pdf"
                    >
                      <Download size={15} strokeWidth={1.6} /> PDF (Print-Ready)
                    </a>
                    <a
                      href={osUnlocked.url}
                      download={osUnlocked.filename}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-4 text-xs uppercase tracking-[0.22em]"
                      data-testid="one-sheet-download-image"
                    >
                      <Download size={15} strokeWidth={1.6} /> Image (PNG)
                    </a>
                  </div>
                </div>
              ) : (
                <form onSubmit={submitOneSheet} className="surface p-8 grid sm:grid-cols-2 gap-5" data-testid="one-sheet-form">
                  <div className="sm:col-span-2">
                    <Label className="eyebrow">Full Name</Label>
                    <Input
                      value={osForm.name}
                      onChange={(e) => setOsForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Your name"
                      className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 mt-1"
                      data-testid="one-sheet-name"
                    />
                  </div>
                  <div>
                    <Label className="eyebrow">Email</Label>
                    <Input
                      type="email"
                      value={osForm.email}
                      onChange={(e) => setOsForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="you@email.com"
                      className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 mt-1"
                      data-testid="one-sheet-email"
                    />
                  </div>
                  <div>
                    <Label className="eyebrow">Phone</Label>
                    <Input
                      type="tel"
                      value={osForm.phone}
                      onChange={(e) => setOsForm((f) => ({ ...f, phone: e.target.value }))}
                      placeholder="(215) 555-0123"
                      className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 mt-1"
                      data-testid="one-sheet-phone"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Button
                      type="submit"
                      disabled={osSubmitting}
                      className="btn-gold rounded-none w-full sm:w-auto inline-flex items-center gap-2.5 px-8 py-4 text-xs uppercase tracking-[0.22em]"
                      data-testid="one-sheet-submit"
                    >
                      <Download size={15} strokeWidth={1.6} />
                      {osSubmitting ? "Unlocking…" : "Get the One-Sheet"}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* INTENT ROUTER — 1-click paths for every visitor */}
      <section className="relative border-b border-white/5" data-testid="epk-intent-router">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-8 lg:py-10">
          <p className="eyebrow mb-5">What brings you here?</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <button
              type="button"
              onClick={() => goBook("music")}
              className="group surface p-5 flex items-center gap-4 text-left transition-all hover:border-[color:var(--gold)]/45 hover:-translate-y-0.5"
              data-testid="intent-book-performance"
            >
              <Mic2 size={20} style={{ color: "var(--gold)" }} />
              <span className="flex-1">
                <span className="block font-serif text-lg tracking-tighter leading-tight">Book a Performance</span>
                <span className="block text-[11px] text-white/45 mt-0.5">Live shows · features</span>
              </span>
              <ArrowRight size={15} className="text-white/30 group-hover:text-[color:var(--gold)] group-hover:translate-x-0.5 transition-all" />
            </button>

            <button
              type="button"
              onClick={() => goBook("speaking")}
              className="group surface p-5 flex items-center gap-4 text-left transition-all hover:border-[color:var(--gold)]/45 hover:-translate-y-0.5"
              data-testid="intent-speaking"
            >
              <Briefcase size={20} style={{ color: "var(--gold)" }} />
              <span className="flex-1">
                <span className="block font-serif text-lg tracking-tighter leading-tight">Speaking / Keynote</span>
                <span className="block text-[11px] text-white/45 mt-0.5">Panels · workshops</span>
              </span>
              <ArrowRight size={15} className="text-white/30 group-hover:text-[color:var(--gold)] group-hover:translate-x-0.5 transition-all" />
            </button>

            <a
              href={CLARITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group surface p-5 flex items-center gap-4 text-left transition-all hover:border-[color:var(--gold)]/45 hover:-translate-y-0.5"
              data-testid="intent-consulting"
            >
              <Sparkles size={20} style={{ color: "var(--gold)" }} />
              <span className="flex-1">
                <span className="block font-serif text-lg tracking-tighter leading-tight">Consulting</span>
                <span className="block text-[11px] text-white/45 mt-0.5">Start: Clarity Assessment</span>
              </span>
              <ArrowUpRight size={15} className="text-white/30 group-hover:text-[color:var(--gold)] transition-colors" />
            </a>

            <button
              type="button"
              onClick={() => scrollTo("shows")}
              className="group surface p-5 flex items-center gap-4 text-left transition-all hover:border-[color:var(--gold)]/45 hover:-translate-y-0.5"
              data-testid="intent-shows"
            >
              <Calendar size={20} style={{ color: "var(--gold)" }} />
              <span className="flex-1">
                <span className="block font-serif text-lg tracking-tighter leading-tight">Upcoming Shows</span>
                <span className="block text-[11px] text-white/45 mt-0.5">Catch Nina live</span>
              </span>
              <ArrowRight size={15} className="text-white/30 group-hover:text-[color:var(--gold)] group-hover:translate-x-0.5 transition-all" />
            </button>
          </div>
        </div>
      </section>

      {/* BIO + TIMELINE */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-6">
            <SectionLabel label="The Bio" />
            <h2 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light">
              Thirty years.
              <br />
              <em className="italic" style={{ color: "var(--gold)" }}>
                One open door.
              </em>
            </h2>
            <p className="font-serif italic text-xl lg:text-2xl text-white/80 leading-relaxed border-l-2 pl-5" style={{ borderColor: "var(--gold)" }}>
              &ldquo;Philadelphia-born artist, broadcaster, and founder &mdash; thirty
              years turning a mic into a movement.&rdquo;
            </p>
            <p className="text-white/65 leading-relaxed">
              From a 1992 debut tour to founding{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>In Da Streets Radio</em>{" "}
              and the <em className="italic">Inspire Da Streets</em> non-profit, Nina
              built the platform she once waited for &mdash; spanning hip hop, R&amp;B,
              faith records, broadcast, and AI-first business advisory.
            </p>
            <Link
              to="/about#biography"
              className="link-gold inline-flex items-center gap-2 text-sm"
              data-testid="epk-full-bio-link"
            >
              Read the full biography <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            <div className="grid grid-cols-2 gap-4 pt-6">
              <Stat k="30" label="Years In Music" />
              <Stat k="60+" label="Speaking Engagements" />
              <Stat k="120+" label="Podcast Episodes" />
              <Stat k="500+" label="Youth Trained" />
            </div>
          </div>

          <div className="lg:col-span-7" data-testid="career-timeline">
            <ol className="relative border-l border-white/10 pl-8 ml-4 space-y-10">
              {visibleTimeline.map((item, idx) => (
                <li key={`${item.year}-${idx}`} className="relative">
                  <span
                    className="absolute -left-[42px] top-1 w-3 h-3 rounded-full"
                    style={{ background: "var(--gold)" }}
                  />
                  <p
                    className="font-serif text-3xl tracking-tighter"
                    style={{ color: "var(--gold)" }}
                  >
                    {item.year}
                  </p>
                  <h4 className="font-serif text-2xl tracking-tighter mt-1">
                    {item.title}
                  </h4>
                  <p className="text-sm text-white/55 mt-2 max-w-md leading-relaxed">
                    {item.body}
                  </p>
                </li>
              ))}
            </ol>
            <button
              type="button"
              onClick={() => setShowFullTimeline((v) => !v)}
              className="mt-10 ml-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-[color:var(--gold)] transition-colors"
              data-testid="timeline-toggle"
            >
              {showFullTimeline ? "Show key milestones" : "View full career timeline"}
              <ArrowRight
                size={14}
                className={`transition-transform ${showFullTimeline ? "-rotate-90" : "rotate-90"}`}
              />
            </button>
          </div>
        </div>
      </section>

      {/* PRESS & RECOGNITION */}
      {press.length > 0 && (
        <section className="py-24 border-t border-white/5" data-testid="epk-press">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-8 mb-12">
              <div className="lg:col-span-6">
                <SectionLabel label="Press & Recognition" />
                <h2 className="font-serif text-5xl lg:text-6xl tracking-tighter font-light">
                  In the{" "}
                  <em className="italic" style={{ color: "var(--gold)" }}>
                    press.
                  </em>
                </h2>
              </div>
              <div className="lg:col-span-5 lg:col-start-8 flex items-end">
                <p className="text-white/60 leading-relaxed">
                  A few highlights recognizing Nina&rsquo;s work across music, media,
                  and the independent business community.
                </p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="epk-press-highlights">
              {(press.filter((p) => p.featured).length ? press.filter((p) => p.featured) : press).slice(0, 4).map((p, i) => {
                const outlet = (p.source || "").split("\u00b7")[0].trim();
                const Card = (
                  <div className="surface p-6 h-full flex flex-col justify-between gap-4 transition-colors group-hover/pr:border-[color:var(--gold)]/40">
                    <p className="font-serif text-2xl tracking-tight leading-tight">{outlet}</p>
                    {p.title && <p className="text-sm text-white/55 leading-relaxed line-clamp-3">{p.title}</p>}
                    {p.href && (
                      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[color:var(--gold)]">
                        Read <ArrowUpRight size={12} />
                      </span>
                    )}
                  </div>
                );
                return p.href ? (
                  <a key={i} href={p.href} target="_blank" rel="noopener noreferrer" className="group/pr block" data-testid="epk-press-highlight">
                    {Card}
                  </a>
                ) : (
                  <div key={i} className="group/pr" data-testid="epk-press-highlight">{Card}</div>
                );
              })}
            </div>

            <div className="mt-10">
              <Link
                to="/press"
                className="btn-ghost-gold inline-flex items-center gap-2 px-7 py-3 text-xs"
                data-testid="epk-full-press-link"
              >
                See the full press archive <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* MY BIZ CONSULTING */}
      <section className="py-28 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <SectionLabel label="My Biz Consulting" />
            <h2 className="font-serif text-5xl lg:text-6xl tracking-tighter font-light leading-[1]">
              AI-first operations
              <br />
              for the{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                independent.
              </em>
            </h2>
            <p className="text-white/60 leading-relaxed mt-6 max-w-md">
              My Biz Consulting helps creator-owned businesses ship faster
              with the right AI stack. We work with labels, indie artists,
              non-profits, and faith-based organizations.
            </p>
            <ul className="space-y-3 text-sm text-white/65 pt-6">
              <li className="flex gap-3">
                <Sparkles size={14} className="mt-1" style={{ color: "var(--gold)" }} />
                AI workflow audits + tooling recommendations
              </li>
              <li className="flex gap-3">
                <Sparkles size={14} className="mt-1" style={{ color: "var(--gold)" }} />
                Brand systems & creator-economy strategy
              </li>
              <li className="flex gap-3">
                <Sparkles size={14} className="mt-1" style={{ color: "var(--gold)" }} />
                Keynote + workshop facilitation for boards & teams
              </li>
            </ul>
            <a
              href={CLARITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold rounded-none inline-flex items-center gap-2.5 px-8 py-4 text-xs uppercase tracking-[0.25em] mt-8"
              data-testid="my-biz-clarity-cta"
            >
              <Sparkles size={15} strokeWidth={1.6} />
              Take the Clarity Assessment
              <ArrowUpRight size={15} strokeWidth={1.6} />
            </a>
            <p className="text-[11px] text-white/45 mt-3 max-w-md">
              Start free — a short assessment pinpoints exactly where AI moves
              your business first.
            </p>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="surface relative overflow-hidden mx-auto w-full max-w-[480px] lg:max-w-none">
              <img
                src={BIZ}
                alt="Nina Capone — My Biz Consulting · AI-first operations"
                className="block w-full h-auto object-cover object-[center_25%] aspect-square lg:aspect-[4/5] lg:max-h-[640px]"
                loading="lazy"
                data-testid="my-biz-portrait"
              />
            </div>
          </div>
        </div>
      </section>

      {/* UPCOMING SHOWS */}
      <div id="shows" className="scroll-mt-24">
        <UpcomingShows
          heading="Catch Nina Live"
          showLabel="Tour · Appearances"
          limit={9}
          hideWhenEmpty
        />
      </div>

      {/* PRO VAULT TEASER — convert industry visitors into $6.50/mo members */}
      <section className="py-20 border-t border-white/5" data-testid="epk-pro-vault-teaser">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Link
            to="/backstage#pro-vault"
            className="group block surface overflow-hidden transition-all duration-300 hover:border-[color:var(--gold)]/40"
            data-testid="epk-pro-vault-card"
          >
            <div className="grid lg:grid-cols-12 gap-0 items-center">
              <div className="lg:col-span-8 p-8 lg:p-12">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-[color:var(--gold)] mb-5">
                  <ShieldCheck size={14} strokeWidth={1.4} /> For Artists &amp; Industry Pros
                </div>
                <h2 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light leading-[1.02] mb-5">
                  Run the business like Nina.
                  <br />
                  Join the <em className="italic" style={{ color: "var(--gold)" }}>Pro Vault</em>.
                </h2>
                <p className="text-white/60 text-base leading-relaxed max-w-xl mb-7">
                  The same guides, templates, and registration roadmaps Nina built protecting
                  her own catalog — split sheets, royalty &amp; PRO playbooks, and EPK assets.
                  Everything&rsquo;s yours while you&rsquo;re a member.
                </p>
                <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                  <span className="inline-flex items-baseline gap-1.5">
                    <span className="font-serif text-3xl" style={{ color: "var(--gold)" }}>$6.50</span>
                    <span className="text-white/45 text-xs uppercase tracking-[0.2em]">/ month</span>
                  </span>
                  <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[color:var(--gold)] group-hover:gap-3.5 transition-all duration-300">
                    <Download size={14} strokeWidth={1.5} /> Explore the Vault <ArrowRight size={14} strokeWidth={1.5} />
                  </span>
                </div>
              </div>
              <div className="lg:col-span-4 relative hidden lg:block self-stretch min-h-[280px] overflow-hidden">
                <img
                  src={imgUrl("/api/static/pro-vault/business-of-music.png")}
                  alt="Pro Vault industry resources"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a050d] via-[#0a050d]/40 to-transparent" />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* BOOKING FORM */}
      <section
        id="contact"
        className="py-28 border-t border-white/5 scroll-mt-24"
        data-testid="booking-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-6">
              <SectionLabel label="Book Nina" />
              <h2 className="font-serif text-5xl lg:text-6xl tracking-tighter font-light leading-[1]">
                Live shows.
                <br />
                Keynotes.
                <br />
                <em className="italic" style={{ color: "var(--gold)" }}>
                  Consulting.
                </em>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-white/60 leading-relaxed">
                Submit your inquiry below. Our team responds within three
                business days with rates, availability, and a contract draft.
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-3 mb-10">
            {[
              { id: "music", label: "Music Performance", icon: Mic2 },
              { id: "speaking", label: "Keynote Speaking", icon: Briefcase },
              { id: "consulting", label: "Music, Artist & Industry Consulting", icon: Sparkles },
            ].map((opt) => {
              const Icon = opt.icon;
              const active = bookingType === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setBookingType(opt.id)}
                  className={`surface p-6 text-left flex items-center gap-4 transition-all ${
                    active ? "border-[color:var(--gold)] -translate-y-1" : ""
                  }`}
                  data-testid={`booking-type-${opt.id}`}
                >
                  <Icon
                    size={22}
                    style={{ color: active ? "var(--gold)" : "rgba(255,255,255,0.5)" }}
                  />
                  <div>
                    <p className="eyebrow">Type</p>
                    <p
                      className="font-serif text-xl tracking-tighter"
                      style={{ color: active ? "var(--gold)" : "white" }}
                    >
                      {opt.label}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {bookingType === "consulting" && (
            <div
              className="surface p-6 lg:p-7 mb-10 flex flex-col sm:flex-row sm:items-center gap-5 justify-between"
              style={{ borderColor: "rgba(212,175,55,0.4)" }}
              data-testid="consulting-clarity-callout"
            >
              <div className="max-w-xl">
                <p className="eyebrow mb-1.5">Consulting starts here</p>
                <p className="text-sm text-white/70 leading-relaxed">
                  Before we scope an engagement, take the free{" "}
                  <span style={{ color: "var(--gold)" }}>Clarity Assessment</span> — a few
                  quick questions that tell us exactly where to begin. It makes
                  your inquiry below far more actionable.
                </p>
              </div>
              <a
                href={CLARITY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold rounded-none inline-flex items-center gap-2 px-6 py-3.5 text-xs uppercase tracking-[0.25em] shrink-0"
                data-testid="consulting-clarity-link"
              >
                <Sparkles size={14} strokeWidth={1.6} />
                Clarity Assessment
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </a>
            </div>
          )}

          <form
            onSubmit={onSubmit}
            className="surface p-8 lg:p-12 grid sm:grid-cols-2 gap-6"
            data-testid="booking-form"
          >
            <Field label="Your Name" id="b-name">
              <Input
                id="b-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                data-testid="booking-name"
              />
            </Field>
            <Field label="Email" id="b-email">
              <Input
                id="b-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                data-testid="booking-email"
              />
            </Field>
            <Field label="Phone" id="b-phone">
              <Input
                id="b-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                data-testid="booking-phone"
              />
            </Field>
            <Field label="Organization" id="b-org">
              <Input
                id="b-org"
                value={form.organization}
                onChange={(e) =>
                  setForm({ ...form, organization: e.target.value })
                }
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                data-testid="booking-org"
              />
            </Field>
            <Field label="Event Date" id="b-date">
              <Input
                id="b-date"
                type="date"
                value={form.event_date}
                onChange={(e) =>
                  setForm({ ...form, event_date: e.target.value })
                }
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 text-white/80"
                data-testid="booking-date"
              />
            </Field>
            <Field label="Location" id="b-loc">
              <Input
                id="b-loc"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                data-testid="booking-location"
              />
            </Field>
            <Field label="Budget Range" id="b-budget" full>
              <Input
                id="b-budget"
                placeholder="$100/hr+"
                value={form.budget}
                onChange={(e) => setForm({ ...form, budget: e.target.value })}
                className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                data-testid="booking-budget"
              />
            </Field>
            <Field label="Tell Us About The Event" id="b-msg" full>
              <Textarea
                id="b-msg"
                rows={5}
                required
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="bg-transparent border border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
                data-testid="booking-message"
              />
            </Field>
            <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-4 pt-2">
              <p className="text-xs text-white/40 flex items-center gap-2">
                <Calendar size={12} /> Reviewed within 3 business days
              </p>
              <Button
                type="submit"
                disabled={submitting}
                className="btn-gold px-10 py-6 text-xs rounded-none"
                data-testid="booking-submit"
              >
                <Send size={14} />
                {submitting ? "Sending…" : "Send Inquiry"}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* FAMILY & LEGACY — points to the full story on /about */}
      <section className="py-24 border-t border-white/5" data-testid="epk-legacy-callout">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <div className="surface p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center gap-8">
            <div className="flex-1 space-y-4">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-[color:var(--gold)]">
                <Heart size={14} strokeWidth={1.4} /> Family & Legacy
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl tracking-tighter font-light leading-[1.02]">
                A Philadelphia music inheritance.
              </h2>
              <p className="text-white/60 leading-relaxed max-w-2xl">
                Nina&rsquo;s sound was raised in the room &mdash; a vocalist mother, a DJ father, a
                Fat Larry&rsquo;s Band guitarist step-father, and the mentorship of the late{" "}
                <em className="italic">Loni Gamble</em>. The full heritage and tribute live on the About page.
              </p>
            </div>
            <div className="flex flex-col gap-3 shrink-0">
              <Link
                to="/about#heritage"
                className="btn-ghost-gold inline-flex items-center justify-between gap-3 px-6 py-3 text-xs"
                data-testid="epk-legacy-heritage-link"
              >
                The Heritage <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
              <Link
                to="/about#dedication"
                className="inline-flex items-center justify-between gap-3 px-6 py-3 text-xs uppercase tracking-[0.25em] text-white/60 hover:text-[color:var(--gold)] transition-colors"
                data-testid="epk-legacy-dedication-link"
              >
                Tribute to Loni Gamble <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ k, label }) {
  return (
    <div>
      <div
        className="font-serif text-4xl tracking-tighter"
        style={{ color: "var(--gold)" }}
      >
        {k}
      </div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mt-1">
        {label}
      </div>
    </div>
  );
}

function Field({ label, id, full, children }) {
  return (
    <div className={`space-y-2 ${full ? "sm:col-span-2" : ""}`}>
      <Label htmlFor={id} className="eyebrow">
        {label}
      </Label>
      {children}
    </div>
  );
}
